import React, { PureComponent } from 'react';
import pt from 'prop-types';
import { Form, Field } from 'components/form';
import View from 'components/view';
import FileReader from 'components/form/file-reader';
import ImageCropper from 'components/form/image-cropper';
import UserPhoto from 'components/user/photo';
import ProfilePage from 'components/profile/profile';

import './settings.scss';

export default class User extends PureComponent {
  static propTypes = {
    router: pt.object,
    onUploadPhoto: pt.func,
    onTransferOwnership: pt.func,
    onResendInvitation: pt.func,
    onChangePassword: pt.func,
    onRemoveUser: pt.func,
    onSubmitUser: pt.func,
  };

  static contextTypes = {
    partner: pt.object.isRequired,
    user: pt.object.isRequired,
    uploadImages: pt.func.isRequired,
    openModal: pt.func.isRequired,
    isOwner: pt.bool.isRequired,
    session: pt.object,
  };

  constructor(props) {
    super(props);

    const user = this.getUser(props);

    this.state = {
      edited: false,
      uploadedPhoto: null,
      user,
      values: { ...user },
    };
  }

  getUser(props = this.props) {
    const {
      users,
      params: { id },
    } = props;

    return users.results?.find((user) => user.id === id);
  }

  onUploadPhoto = async (files) => {
    const { uploadImages } = this.context;
    const results = await uploadImages(files);

    this.setState({ uploadedPhoto: { file: results[0] } });
  };

  onCloseCropper = () => {
    this.setState({ uploadedPhoto: null });
  };

  onSubmitCropper = async (file) => {
    const { onUploadPhoto } = this.props;

    if (!file) {
      return;
    }

    await onUploadPhoto(file);

    this.onCloseCropper();
  };

  onClickResendInvitation = (event) => {
    event.preventDefault();

    const { onResendInvitation } = this.props;
    const { user } = this.state;

    onResendInvitation(user.id);
  };

  onClickTransferOwnership = (event) => {
    event.preventDefault();

    const { onTransferOwnership } = this.props;
    const { user } = this.state;

    onTransferOwnership(user.id);
  };

  onClickChangePassword = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { onChangePassword } = this.props;

    openModal('ChangePassword', {
      onSubmit: onChangePassword,
    });
  };

  onClickRemoveUser = async (event) => {
    event.preventDefault();

    const { router, onRemoveUser } = this.props;
    const { user } = this.state;

    const result = await onRemoveUser(user.id);

    if (result) {
      router.replace('/users');
    }
  };

  onChange = (values, edited) => {
    this.setState({ values: { ...values }, edited });
  };

  // Own-profile submit: route password changes and strip the read-only
  // username along with the local-only theme.
  onSubmitOwnProfile = async (values) => {
    const { onSubmitUser, onChangePassword } = this.props;

    if (values.password != null) {
      return onChangePassword(values.password);
    }

    const rest = { ...values };
    delete rest.theme;
    delete rest.username;
    const result = await onSubmitUser(rest);
    return result !== false;
  };

  onUploadOwnPhoto = (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    return this.props.onUploadPhoto(file);
  };

  render() {
    const { user: userSelf, partner, isOwner, session } = this.context;
    const { onSubmitUser } = this.props;
    const { user, uploadedPhoto, edited } = this.state;
    const isOwnerUser = partner.owner_id === user.id;
    const isSelf = userSelf.id === user.id;

    // Own profile reuses the admin ProfilePage; other users keep the view below.
    if (isSelf) {
      return (
        <ProfilePage
          // Partner users carry `email`, not `username`.
          user={{ ...user, username: user.username || user.email }}
          session={session}
          edited={edited}
          onUploadPhoto={this.onUploadOwnPhoto}
          onChangeForm={this.onChange}
          onSubmitProfile={this.onSubmitOwnProfile}
          hidePreferences
          themeScope="local"
          detailsReadOnly
          // Saved on this partner's authorization, not the user record
          preferences={
            <div className="row">
              <Field
                type="checkbox"
                name="notify_updates"
                label="Notify me about updates"
                defaultChecked={Boolean(user.notify_updates)}
              />
            </div>
          }
        />
      );
    }

    const canResendInvitation = !user.date_last_login;
    // Any member can remove any user; only the account owner is protected
    const canRemove = !isOwnerUser && !isSelf;
    const canTransferOwnership = isOwner && !isSelf && !canResendInvitation;

    return (
      <div className="settings settings-user">
        <Form onSubmit={onSubmitUser} onChange={this.onChange}>
          <View
            full
            headerTitle={user.name || user.username || 'Profile'}
            uri="/users"
            sectionTitle="Users"
            headerActions={[
              {
                label: 'Save profile',
                type: edited ? 'default' : 'secondary disabled',
                submit: true,
              },
            ]}
            extraActions={[
              {
                label: 'Resend invitation',
                onClick: this.onClickResendInvitation,
                hidden: !canResendInvitation,
              },
              {
                label: 'Transfer ownership',
                onClick: this.onClickTransferOwnership,
                hidden: !canTransferOwnership,
              },
              {
                label: 'Change password',
                onClick: this.onClickChangePassword,
                hidden: !isSelf,
              },
              {
                label: 'Remove from account',
                onClick: this.onClickRemoveUser,
                hidden: !canRemove,
                className: 'danger',
                delete: true,
              },
            ]}
          >
            <div className="user-view">
              <div className="user-container">
                <div className="view-body-section-group">
                  <div className="view-body-section">
                    <div className="view-section-content no-header">
                      <div className="user-details">
                        <div className="user-photo-container">
                          <FileReader
                            accept="image/*"
                            onFiles={this.onUploadPhoto}
                            multiple={false}
                            disabled={!isSelf}
                          >
                            <UserPhoto user={user} width={72} height={72} />
                          </FileReader>
                        </div>
                        <h3 className="view-header-name">
                          {user.name || user.email}{' '}
                        </h3>
                        <div className="view-header-note">
                          {user.name && user.email}
                        </div>
                        <div className="status positive">
                          {isOwnerUser ? 'Owner' : 'Member'}
                        </div>
                      </div>
                      <div className="user-form-fields">
                        <fieldset>
                          {(isSelf || user.name) && (
                            <Field
                              type="text"
                              name="name"
                              label="Name"
                              required={true}
                              disabled={!isSelf}
                              defaultValue={user.name}
                            />
                          )}

                          <Field
                            type="text"
                            name="email"
                            label="Email address"
                            required={true}
                            disabled={!isSelf}
                            defaultValue={user.email}
                          />

                          {isSelf && (
                            <Field
                              type="checkbox"
                              name="notify_updates"
                              label="Receive updates by email"
                              defaultChecked={user.notify_updates}
                            />
                          )}
                        </fieldset>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </View>
        </Form>
        {uploadedPhoto && (
          <ImageCropper
            image={uploadedPhoto}
            onClose={this.onCloseCropper}
            onSubmit={this.onSubmitCropper}
          />
        )}
      </div>
    );
  }
}
